// @flow
import React from 'react';
import { connect } from 'react-redux';
import { noop, get } from 'lodash';
import useSheet from 'app/jss';
import t from 'app/utils/translate';
import * as FriendRequestActions from 'app/actions/friendRequestActions';
import FriendGroupActionPanel from 'app/components/friends/friendCard/groups/FriendGroupActionPanel';
import interactableFriendGroupsSelector from 'app/selectors/friends/interactableFriendGroupsSelector';
import FriendIdentity from 'app/components/friends/common/FriendIdentity';
import FriendDisabledOverlay from 'app/components/friends/common/FriendDisabledOverlay';
import * as FriendActions from 'app/actions/friendsActions';
import { THEY_REQUESTED, YOU_REQUESTED } from 'app/constants/friendStatusTypes';
import FriendConfirmBox from 'app/components/friends/common/FriendConfirmBox';
import FriendYouRequestPanel from './panels/FriendYouRequestPanel';
import FriendTheyRequestPanel from './panels/FriendTheyRequestPanel';
import FriendDetailsPanel from './panels/FriendDetailsPanel';
import FriendDetailsContent from './FriendDetailsContent';
import FriendGroups from './groups/FriendGroups';
import FriendDetailsHeader from './FriendDetailsHeader';
import CardContentMessage from './CardContentMessage';

type Props = {
  request: FriendRequest,
  classes: Object,
  onClick?: (id: number) => void,
  friendGroups?: Array<Object>,
  onDeleteFriendRequest: (friendId: number) => void,
  onBack: (friend: *) => Promise<Object>,
};

type State = {
  rejecting: boolean,
};

class FriendRequestDetails extends React.Component {
  props: Props;
  state: State = {
    deleting: false,
  };

  static defaultProps = {
    onClick: noop,
    friendGroups: [],
  };

  onBack = () => {
    this.props.onBack();
  };

  onDeleteRequest = () => {
    // remove the friend
    this.setState({ deleting: true });
  };

  onConfirmAction = () => {
    const { onDeleteFriendRequest, request } = this.props;
    if (this.state.deleting) {
      onDeleteFriendRequest(request.id);
    }
  };

  onCancelAction = () => {
    this.setState({ deleting: false });
  };

  getSubTitleText() {
    const { request } = this.props;
    const { deleting, rejecting } = this.state;

    if (deleting) {
      return t('Are you sure you want to remove this request?');
    }

    if (rejecting) {
      return t('Are you sure you want to reject this request?');
    }

    if (request.status === THEY_REQUESTED) {
      return t('sent you a friend request');
    }
    return t('pending friend request');
  }

  render() {
    const { classes, onClick, request, friendGroups } = this.props;
    const { deleting } = this.state;

    const isOtherState = deleting;
    const theyRequested = request.status === THEY_REQUESTED;
    const youRequested = request.status === YOU_REQUESTED;

    const isDisabled = isOtherState || theyRequested || youRequested;
    return (
      <div className={classes.friendRequestDetails}>
        <FriendDetailsHeader onClick={onClick}>
          <FriendIdentity friend={request} subtitle={this.getSubTitleText()} />
        </FriendDetailsHeader>
        <FriendDetailsPanel isOtherState={isOtherState}>
          {theyRequested &&
            !isOtherState && <FriendTheyRequestPanel requestId={request.id} key="theyrequested" />}
          {youRequested &&
            !isOtherState && (
              <FriendYouRequestPanel onDeleteRequest={this.onDeleteRequest} key="yourequested" />
            )}
          {isOtherState && (
            <FriendConfirmBox
              onConfirm={this.onConfirmAction}
              onCancel={this.onCancelAction}
              className={classes.confirmBox}
              key="confirm"
            />
          )}
        </FriendDetailsPanel>
        {theyRequested && (
          <FriendDetailsContent>
            <CardContentMessage
              className={classes.bottomPadding}
              title={`${t('A friend is waiting')}...`}
            >
              Accepting this person as a friend allows you and them to quickly find each other on
              the leaderboard.
            </CardContentMessage>
          </FriendDetailsContent>
        )}
      </div>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    inlineSpaced: {
      '& >:not(:last-child)': {
        marginRight: 10,
      },
    },
    friendRequestDetails: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontSmoothing: 'antialiased',
    },
    back: {
      position: 'absolute',
      top: 10,
      left: 10,
    },
    confirmBox: {
      backgroundColor: theme.color.basis6,
    },
  };
}

function mapStateToProps(state) {
  return {
    friendGroups: interactableFriendGroupsSelector(state),
  };
}

const actions = {
  onDeleteFriendRequest: FriendRequestActions.removeFriendRequest,
  onRejectFriendRequest: FriendRequestActions.rejectFriendRequest,
  onBack: FriendActions.viewFriend,
};

export default connect(mapStateToProps, actions)(useSheet(applyStyles)(FriendRequestDetails));
